import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  ShieldCheck,
  ShieldAlert,
  Link2,
  ScanLine,
  Loader2,
  UserX,
  MailWarning,
  Bug,
  CheckCircle2,
} from "lucide-react";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <div className="rounded-2xl border border-white/8 bg-bloq-s1 p-5" data-testid={`shield-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <Icon className={cn("mb-3 h-6 w-6", accent)} />
      <div className="font-heading text-3xl font-bold">{value}</div>
      <div className="mt-1 text-xs text-bloq-muted">{label}</div>
    </div>
  );
}

const VERDICT_STYLE = {
  danger: "border-destructive/40 bg-destructive/10 text-destructive",
  warning: "border-amber-500/40 bg-amber-500/10 text-amber-400",
  safe: "border-bloq-mint/30 bg-bloq-mint/10 text-bloq-mint",
};

function ScanResult({ result }) {
  if (!result) return null;
  const threat = result.threat || result.scam_intent;
  return (
    <div className="mt-4 space-y-3" data-testid="scan-result">
      <div
        className={cn(
          "flex items-center gap-2 rounded-xl border px-4 py-3 text-sm font-semibold",
          threat ? VERDICT_STYLE.danger : VERDICT_STYLE.safe,
        )}
      >
        {threat ? <ShieldAlert className="h-5 w-5" /> : <CheckCircle2 className="h-5 w-5" />}
        {threat ? "Threat detected — do not engage" : "Looks clean — no threats detected"}
      </div>
      {result.scam_intent && (
        <div className="rounded-xl border border-destructive/40 bg-destructive/10 px-4 py-2 text-sm text-destructive">
          Scam intent detected in this message.
        </div>
      )}
      {(result.links || []).map((l, i) => (
        <div key={i} className={cn("flex items-start gap-2 rounded-xl border px-4 py-2 text-sm", VERDICT_STYLE[l.verdict] || VERDICT_STYLE.warning)}>
          <Link2 className="mt-0.5 h-4 w-4 shrink-0" />
          <div className="min-w-0">
            <div className="truncate font-mono">{l.url}</div>
            <div className="opacity-90">{l.label}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function ShieldPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [text, setText] = useState("");
  const { data: summary, isLoading } = useQuery({ queryKey: ["shield"], queryFn: api.shieldSummary });
  const scanMut = useMutation({ mutationFn: (t) => api.shieldScan(t) });

  // §9.1 deep link: /app/shield?scan=<url> pre-fills the scanner and runs it (from the link interstitial).
  useEffect(() => {
    const pre = searchParams.get("scan");
    if (!pre) return;
    setText(pre);
    scanMut.mutate(pre);
    const n = new URLSearchParams(searchParams);
    n.delete("scan");
    setSearchParams(n, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stats = summary?.stats || {};
  const protection = summary?.protection_score ?? 0;

  return (
    <div className="h-full overflow-y-auto bg-bloq-bg">
      <div className="mx-auto max-w-3xl px-5 py-6 sm:px-8">
        <h1 className="text-2xl font-bold">Shield</h1>
        <div className="mt-0.5 flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider text-bloq-mint">
          <ShieldCheck className="h-3 w-3" /> Your live protection layer
        </div>

        {isLoading ? (
          <div className="flex justify-center py-16 text-bloq-muted">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : (
          <>
            {/* Protection score */}
            <div className="relative mt-6 overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-b from-bloq-s2 to-bloq-s1 p-7" data-testid="protection-score-card">
              <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-bloq-mint/15 blur-3xl" />
              <div className="relative flex items-center gap-6">
                <div className="relative flex h-28 w-28 shrink-0 items-center justify-center">
                  <svg viewBox="0 0 120 120" className="h-28 w-28 -rotate-90">
                    <bloq cx="60" cy="60" r="52" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="10" />
                    <bloq
                      cx="60" cy="60" r="52" fill="none" stroke="url(#shieldGrad)" strokeWidth="10" strokeLinecap="round"
                      strokeDasharray={2 * Math.PI * 52}
                      strokeDashoffset={2 * Math.PI * 52 * (1 - protection / 100)}
                    />
                    <defs>
                      <linearGradient id="shieldGrad" x1="0" y1="0" x2="1" y2="1">
                        <stop offset="0%" stopColor="#01FABD" />
                        <stop offset="100%" stopColor="#3591F4" />
                      </linearGradient>
                    </defs>
                  </svg>
                  <div className="absolute font-heading text-3xl font-bold">{protection}</div>
                </div>
                <div>
                  <div className="text-xs font-bold uppercase tracking-wider text-bloq-muted">Protection score</div>
                  <div className="mt-1 text-lg font-semibold text-bloq-mint">You're well protected</div>
                  <p className="mt-1 text-sm text-bloq-muted">
                    BLOQ Shield is actively screening your links, messages and connection requests.
                  </p>
                </div>
              </div>
            </div>

            {/* Stats */}
            <div className="mt-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
              <StatCard icon={Link2} label="Links scanned" value={stats.links_scanned ?? 0} accent="text-bloq-blue" />
              <StatCard icon={ShieldAlert} label="Threats blocked" value={stats.threats_blocked ?? 0} accent="text-destructive" />
              <StatCard icon={MailWarning} label="Requests quarantined" value={stats.requests_quarantined ?? 0} accent="text-amber-400" />
              <StatCard icon={Bug} label="Reports filed" value={stats.reports_filed ?? 0} accent="text-bloq-purple" />
            </div>

            <div className="mt-3 grid grid-cols-2 gap-3">
              <div className="rounded-2xl border border-white/8 bg-bloq-s1 p-4">
                <div className="text-xs text-bloq-muted">DM policy</div>
                <div className="mt-1 font-semibold capitalize">{summary?.privacy?.dm_policy || "contacts"}</div>
              </div>
              <div className="rounded-2xl border border-white/8 bg-bloq-s1 p-4">
                <div className="flex items-center gap-1 text-xs text-bloq-muted">
                  <UserX className="h-3 w-3" /> Blocked users
                </div>
                <div className="mt-1 font-semibold">{summary?.blocked_count ?? 0}</div>
              </div>
            </div>

            {/* Scanner tool */}
            <div className="mt-6 rounded-3xl border border-white/8 bg-bloq-s1 p-6" data-testid="shield-scanner">
              <div className="flex items-center gap-2">
                <ScanLine className="h-5 w-5 text-bloq-mint" />
                <h2 className="text-lg font-semibold">Scan a message</h2>
              </div>
              <p className="mt-1 text-sm text-bloq-muted">
                Paste any message or link and let Shield check it for scams and phishing.
              </p>
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Paste a suspicious message or link…"
                rows={3}
                className="mt-4 w-full rounded-xl border border-white/10 bg-bloq-bg p-3 text-sm text-white outline-none focus:border-bloq-mint/40"
                data-testid="shield-scan-input"
              />
              <button
                onClick={() => text.trim() && scanMut.mutate(text.trim())}
                disabled={!text.trim() || scanMut.isPending}
                className="mt-3 flex items-center gap-2 rounded-full bloq-gradient px-6 py-2.5 text-sm font-bold text-bloq-bg transition-opacity hover:opacity-90 disabled:opacity-40"
                data-testid="shield-scan-btn"
              >
                {scanMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <ScanLine className="h-4 w-4" />}
                Run Shield scan
              </button>
              <ScanResult result={scanMut.data} />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
